import { Injectable, Injector } from '@angular/core';
import { OrigamiHttpResponse } from '@app/origami-api/services/base/service.base';
import { LocalStorageService } from '@app/services/local-storage.service';
import { isEqual } from 'lodash';
import { environment } from '../../../../environments/environment';
import { Subject } from 'rxjs';

/**
 * Cache service for HTTP POST requests
 *
 * Provides:
 * - POST request caching (generates keys from URL + body hash)
 * - Request deduplication (concurrent identical requests share the same Subject)
 * - Service-level cache buckets for granular cache invalidation
 * - Consumer tracking to keep requests alive while subscribers wait
 */
@Injectable({
  providedIn: 'root',
})
export class CacheService {
  isEnabled = true;

  /** Response cache storage */
  private responseCache = new Map<string, OrigamiHttpResponse>();

  /** Cache buckets per service for grouped invalidation */
  private buckets = new Map<string, Set<string>>();

  /** In-flight request Subjects for request deduplication, with timestamp and consumer count */
  private inFlightRequests = new Map<
    string,
    { subject: Subject<OrigamiHttpResponse>; timestamp: number; consumerCount: number }
  >();

  /** Maximum time (ms) an in-flight request can be pending before considered stale */
  private readonly IN_FLIGHT_TTL = 2 * 60 * 1000; // 2 minute

  /** Maps cache keys to their body for deep equality checks */
  private bodyRegistry = new Map<string, { url: string; body: any }>();

  constructor(private localStorageService: LocalStorageService) {
    if (!this.localStorageService.hasItem(this.localStorageService.keys.ti_cache_enabled)) {
      this.localStorageService.setItem(this.localStorageService.keys.ti_cache_enabled, this.isEnabled);
    } else {
      this.isEnabled = JSON.parse(this.localStorageService.getItem(this.localStorageService.keys.ti_cache_enabled));
    }
  }

  /**
   * Generates a unique cache key from service URL, endpoint URL, and request body
   */
  private generateCacheKey(serviceUrl: string, url: string, body: any): string {
    const bodyHash = this.hashBody(body);
    return `${serviceUrl}::${url}::${bodyHash}`;
  }

  /**
   * Creates a hash from the request body for cache key generation.
   * Uses djb2 hash algorithm which processes the entire JSON string to prevent collisions.
   */
  private hashBody(body: any): string {
    if (!body) return 'empty';
    try {
      const json = JSON.stringify(body);
      // djb2 hash algorithm - processes entire string to avoid truncation collisions
      let hash = 5381;
      for (let i = 0; i < json.length; i++) {
        // hash * 33 + charCode (using bitwise for performance)
        hash = (hash << 5) + hash + json.charCodeAt(i);
        // Keep hash in 32-bit range
        hash = hash >>> 0;
      }
      // Convert to base36 for compact representation
      return hash.toString(36);
    } catch {
      return 'unhashable';
    }
  }

  /**
   * Gets or creates a cache bucket for a service
   */
  private getBucket(serviceUrl: string): Set<string> {
    if (!this.buckets.has(serviceUrl)) {
      this.buckets.set(serviceUrl, new Set<string>());
    }
    return this.buckets.get(serviceUrl)!;
  }

  /**
   * Formats URL for logging by extracting the relevant part
   */
  private formatUrlForLog(url: string, serviceUrl: string): string {
    const index = url.indexOf(serviceUrl);
    return index >= 0 ? url.slice(index) : url;
  }

  /**
   * Logs cache operations in a grouped, collapsible format
   */
  private _log(
    type: 'hit' | 'miss' | 'in-flight' | 'stale' | 'collision' | 'saved' | 'consumer',
    url: string,
    serviceUrl: string,
    details?: string,
  ): void {
    const useLog = false;
    if (environment.production || !useLog) return;

    const formattedUrl = this.formatUrlForLog(url, serviceUrl);
    const timestamp = new Date().toLocaleTimeString();

    // console.groupCollapsed(`[Cache ${type}] ${formattedUrl} - ${timestamp}`);
    // console.log('Operation:', type);
    // console.log('URL:', formattedUrl);
    // if (details) {
    //   console.log('Details:', details);
    // }
    // console.groupEnd();
  }

  /**
   * Verifies that a cached entry matches the request body using deep equality.
   * @returns true if the body matches, false if hash collision detected.
   */
  private verifyBodyMatch(cacheKey: string, body: any): boolean {
    const registered = this.bodyRegistry.get(cacheKey);
    return registered ? isEqual(registered.body, body) : false;
  }

  /**
   * Fallback: Scans all entries for a service to find matching body.
   * Only used when hash collision is detected.
   */
  private findMatchingEntryFallback(
    serviceUrl: string,
    url: string,
    body: any,
    excludeKey: string,
  ): OrigamiHttpResponse | null {
    const bucket = this.buckets.get(serviceUrl);
    if (!bucket) return null;

    for (const key of bucket) {
      if (key === excludeKey) continue; // Already checked this one
      const registered = this.bodyRegistry.get(key);
      if (registered && registered.url === url && isEqual(registered.body, body)) {
        return this.responseCache.get(key) ?? null;
      }
    }
    return null;
  }

  get(
    serviceUrl: string,
    url: string,
    body: any,
    silent?: boolean,
  ): OrigamiHttpResponse | Subject<OrigamiHttpResponse> | null {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);

    // Check for in-flight request first (request deduplication)
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      // Check if the in-flight request is stale (possible cancelled request)
      if (Date.now() - inFlight.timestamp > this.IN_FLIGHT_TTL) {
        this.inFlightRequests.delete(cacheKey);
        this._log('stale', url, serviceUrl, 'Removed stale in-flight request');
      } else {
        // Verify body match (guard against hash collision)
        if (this.verifyBodyMatch(cacheKey, body)) {
          if (!silent) {
            this._log('in-flight', url, serviceUrl, 'Request already in progress - sharing subject');
          }
          return inFlight.subject;
        }
        // Hash collision on in-flight - rare, let it make a new request
      }
    }

    // O(1) direct lookup by cache key
    const cachedResponse = this.responseCache.get(cacheKey);
    if (cachedResponse) {
      // Verify body match (guard against hash collision)
      if (this.verifyBodyMatch(cacheKey, body)) {
        if (!silent) {
          this._log('hit', url, serviceUrl, 'Returning cached response');
        }
        return cachedResponse;
      }
      // Hash collision - fall back to full scan
      this._log('collision', url, serviceUrl, 'Hash collision detected - performing full scan');
      const fallbackResponse = this.findMatchingEntryFallback(serviceUrl, url, body, cacheKey);
      if (fallbackResponse) {
        return fallbackResponse;
      }
    }

    return null;
  }

  put(serviceUrl: string, url: string, body: any, response: OrigamiHttpResponse | Subject<OrigamiHttpResponse>): void {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);
    const bucket = this.getBucket(serviceUrl);

    if (response instanceof Subject) {
      // Store in-flight request Subject for deduplication with timestamp and initial consumer count
      this.inFlightRequests.set(cacheKey, { subject: response, timestamp: Date.now(), consumerCount: 1 });
      bucket.add(cacheKey);
      this.bodyRegistry.set(cacheKey, { url, body });
    } else {
      // Remove from in-flight requests
      this.inFlightRequests.delete(cacheKey);

      // Store completed response in cache
      this.responseCache.set(cacheKey, response);
      bucket.add(cacheKey);
      this.bodyRegistry.set(cacheKey, { url, body });

      this._log('saved', url, serviceUrl, 'Response cached successfully');
    }
  }

  /**
   * Adds a consumer to an in-flight request.
   * Call when a new subscriber joins an existing in-flight request.
   */
  addConsumer(serviceUrl: string, url: string, body: any): void {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      inFlight.consumerCount++;
      this._log('consumer', url, serviceUrl, `Added consumer (count: ${inFlight.consumerCount})`);
    }
  }

  /**
   * Removes a consumer from an in-flight request.
   * @returns The remaining consumer count, or -1 if no in-flight request exists.
   */
  removeConsumer(serviceUrl: string, url: string, body: any): number {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      inFlight.consumerCount--;
      this._log('consumer', url, serviceUrl, `Removed consumer (count: ${inFlight.consumerCount})`);
      return inFlight.consumerCount;
    }
    return -1;
  }

  /**
   * Gets the current consumer count for an in-flight request.
   * @returns The consumer count, or 0 if no in-flight request exists.
   */
  getConsumerCount(serviceUrl: string, url: string, body: any): number {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);
    const inFlight = this.inFlightRequests.get(cacheKey);
    return inFlight?.consumerCount ?? 0;
  }

  /**
   * Removes an in-flight request entry and notifies waiting subscribers.
   * Call when request errors or is cancelled with no remaining consumers.
   * @param error - Error to emit to waiting subscribers. If provided, Subject.error() is called.
   */
  removeInFlight(serviceUrl: string, url: string, body: any, error?: any): void {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);
    const inFlight = this.inFlightRequests.get(cacheKey);
    if (inFlight) {
      // Notify waiting subscribers that the request failed/was cancelled
      inFlight.subject.error(error ?? { cancelled: true, message: 'Request was cancelled' });
    }
    this.inFlightRequests.delete(cacheKey);
  }

  /**
   * Cleans up an in-flight request entry without notifying subscribers.
   * Call when request completes but response should not be cached.
   */
  cleanupInFlight(serviceUrl: string, url: string, body: any): void {
    const cacheKey = this.generateCacheKey(serviceUrl, url, body);
    this.inFlightRequests.delete(cacheKey);
  }

  dropCacheSection(serviceUrl: string): void {
    const bucket = this.buckets.get(serviceUrl);
    if (bucket) {
      // Clear all entries for this service
      for (const key of bucket) {
        this.inFlightRequests.delete(key);
        this.bodyRegistry.delete(key);
        this.responseCache.delete(key);
      }
      this.buckets.delete(serviceUrl);
    }
  }

  dropAllCache(): void {
    this.inFlightRequests.clear();
    this.bodyRegistry.clear();
    this.responseCache.clear();
    this.buckets.clear();
  }
}

export const cacheService = Injector.create({
  providers: [
    { provide: CacheService, deps: [LocalStorageService] },
    { provide: LocalStorageService, deps: [] },
  ],
}).get(CacheService);
